<template>
  <div class="list col-md-4" :style="{ zIndex: showDropdown ? '1001' : 'auto' }">
    <LoadingSpinner v-if="isReordering" :text="$t.get('Rearranging tasks...')" />
    
    <h2>
      <div class="list-buttons">
        <div v-if="id !== null" class="dropdown">
          <button class="dropdown-toggle" @click="toggleDropdown" :disabled="isReordering">
            <i class="fa fa-cog" />
          </button>
          <div class="dropdown-menu" :class="{ show: showDropdown }">
            <a class="dropdown-item" @click="onEdit">
              <i class="fa fa-edit" /> {{ $t.get('Edit') }}
            </a>
            <a class="dropdown-item" @click="rearrangeByDate">
              <i class="fa fa-calendar" /> {{ $t.get('Rearrange by date') }}
            </a>
            <div class="dropdown-divider" />
            <a class="dropdown-item text-danger" @click="onDelete">
              <i class="fa fa-trash" /> {{ $t.get('Delete') }}
            </a>
          </div>
        </div>
        <button v-if="id !== null && !selectedTaskListId" @click="openTaskList">
          <i class="fa fa-arrow-right" />
        </button>
        <button v-if="selectedTaskListId" @click="closeTaskList">
          <i class="fa fa-arrow-left" />
        </button>
      </div>
      <span class="header" @click="hidden = !hidden">{{ name }}</span>
      <i
        class="fa header-icon"
        :class="{
          'fa-chevron-down': !hidden,
          'fa-chevron-up': hidden,
        }"
      />
      <div class="task-list-counter">
        <p>
          ({{ openTaskCount }}
          {{
            openTaskCount === 1 ? $t.get('open task') : $t.get('open tasks')
          }})
        </p>
      </div>
    </h2>

    <div :hidden="hidden">
      <draggable
        v-model="modifiedTasks"
        :group="tasksGroup"
        item-key="id"
        @start="onDragStart"
        @end="onDragEnd"
        @add="onAdd"
        @update="onUpdate"
      >
        <template #item="{ element }">
          <task-list-item :task="element" :lists="lists" />
        </template>
      </draggable>
    </div>
  </div>
</template>

<script>
import TaskListItem from '@/components/TaskListItem.vue';
import draggable from 'vuedraggable';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export default {
  components: { TaskListItem, draggable, LoadingSpinner },

  props: {
    tasks: { type: Array, default: () => [] },
    name: { type: String, default: '' },
    id: { type: String, default: null },
    index: { type: Number, default: null },
    showButtons: { type: Boolean, default: true },
    lists: { type: Array, default: null },
    selectedTaskListId: { type: String, default: null },
    editTaskList: { type: Function, default: () => {} },
    deleteTaskList: { type: Function, default: () => {} },
  },
  data: function () {
    return {
      hidden: false,
      modifiedTasks: [],
      drag: false,
      showDropdown: false,
      isReordering: false,
      tasksGroup: {
        name: this.name,
        put: true,
        pull: true,
      },
    };
  },
  computed: {
    openTaskCount() {
      const openTasks = this.modifiedTasks.filter((task) => !task.completed);
      return openTasks.length;
    },
  },
  watch: {
    tasks(oldVal, newVal) {
      this.modifiedTasks = this.tasks;
    },
  },
  created() {
    this.modifiedTasks = this.tasks;
    // Listen for global task updates
    this.emitter = this.$root.emitter || this.emitter;
    
    // Listen for dropdown events from other TaskList components
    this.emitter.on('tasklist:dropdown-opened', (sourceId) => {
      if (sourceId !== this.id) {
        this.showDropdown = false;
        document.removeEventListener('click', this.closeDropdown);
      }
    });
  },
  
  beforeUnmount() {
    // Clean up event listener
    this.emitter.off('tasklist:dropdown-opened');
  },

  mounted: function () {},

  methods: {
    onDragStart(e) {
      this.drag = true;
    },
    onDragEnd(e) {
      this.drag = false;
    },
    onAdd(e) {
      const task_id = e.item.getAttribute('data-task-id');
      this.$api
        .post(`/tasks/${task_id}/list/`, {
          index: e.newIndex,
          list: this.id,
        })
        .then((res) => {
          // this.emitter.emit('tasks-page:update-tasks');
        })
        .catch((err) => {
          console.error(err);
        });
    },
    onUpdate(e) {
      const task_id = e.item.getAttribute('data-task-id');
      this.$api
        .post(`/tasks/${task_id}/list/`, {
          index: e.newIndex,
          list: this.id,
        })
        .then((res) => {
          // this.emitter.emit('tasks-page:update-tasks');
        })
        .catch((err) => {
          console.error(err);
        });
    },

    onEdit() {
      this.showDropdown = false;
      document.removeEventListener('click', this.closeDropdown);
      
      this.editTaskList(this.id);
    },

    onDelete() {
      this.showDropdown = false;
      document.removeEventListener('click', this.closeDropdown);
      
      this.deleteTaskList(this.id);
    },

    openTaskList() {
      this.$router.push(`/tasks/${this.id}`);
    },
    closeTaskList() {
      this.$router.push('/tasks');
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      // Close dropdown when clicking outside
      // And If opening dropdown, notify other TaskList components
      if (this.showDropdown) {
        this.emitter.emit('tasklist:dropdown-opened', this.id);
        
        setTimeout(() => {
          document.addEventListener('click', this.closeDropdown);
        }, 0);
      }
    },
    closeDropdown(event) {
      if (!event.target.closest('.dropdown')) {
        this.showDropdown = false;
        document.removeEventListener('click', this.closeDropdown);
      }
    },
    rearrangeByDate() {
      this.showDropdown = false;
      document.removeEventListener('click', this.closeDropdown);
      
      this.isReordering = true;
      
      // Fetch the latest data from the server
      this.$api.get('/tasks', { completed: true })
        .then(response => {
          // Filter tasks that belong to this list
          const serverTasks = response.data.filter(task => 
            (task.tasklist && task.tasklist.id === this.id) || 
            (!task.tasklist && this.id === null)
          );
          
          // Create a map of current tasks for easy comparison
          const currentTasksMap = {};
          this.modifiedTasks.forEach(task => {
            currentTasksMap[task.id] = task;
          });
          
          // Check if server data differs from current data
          let hasChanges = false;
          
          // Check if tasks count differs
          if (serverTasks.length !== this.modifiedTasks.length) {
            hasChanges = true;
          } else {
            // Check if any task data differs
            for (const serverTask of serverTasks) {
              const currentTask = currentTasksMap[serverTask.id];
              if (!currentTask || 
                  serverTask.completed !== currentTask.completed || 
                  serverTask.due !== currentTask.due || 
                  serverTask.text !== currentTask.text) {
                hasChanges = true;
                break;
              }
            }
          }
          
          // If there are changes, update local tasks with server data
          if (hasChanges) {
            this.modifiedTasks = serverTasks;
          }
          
          // Now proceed with rearranging
          const openTasks = this.modifiedTasks.filter(task => !task.completed);
          const completedTasks = this.modifiedTasks.filter(task => task.completed);
          
          // Group tasks by date
          const withDate = openTasks.filter(task => task.due);
          const withoutDate = openTasks.filter(task => !task.due);
          
          // Sort tasks with dates (most recent first)
          withDate.sort((a, b) => {
            if (a.due === b.due) {
              return a.index - b.index;
            }
            return new Date(a.due) > new Date(b.due) ? 1 : -1;
          });
          
          // Combine all sorted, unsorted and completed tasks
          const rearrangedTasks = [...withDate, ...withoutDate, ...completedTasks];
          
          // Check if the order has actually changed
          let orderChanged = false;
          if (rearrangedTasks.length !== this.modifiedTasks.length) {
            orderChanged = true;
          } else {
            for (let i = 0; i < rearrangedTasks.length; i++) {
              if (rearrangedTasks[i].id !== this.modifiedTasks[i].id) {
                orderChanged = true;
                break;
              }
            }
          }
          
          // Only update if there are actual changes to the order
          if (orderChanged) {
            // Save the new order to the server
            this.bulkUpdateTaskOrder(rearrangedTasks);
          } else {
            // No changes needed, just reset loading state
            this.isReordering = false;
          }
        })
        .catch(err => {
          console.error('Error fetching latest tasks:', err);
          this.isReordering = false; // Make sure to reset loading state on error
        });
    },
    bulkUpdateTaskOrder(tasks) {
      const taskUpdates = tasks.map((task, index) => ({
        id: task.id,
        index: index,
        list: this.id
      }));
      
      this.$api.post('/tasks/bulk-update-order/', { tasks: taskUpdates })
        .then(response => {
          // Update local task list
          this.modifiedTasks = tasks;
          // Instead of notifying parent to refresh all tasks, emit a more specific event
          this.$emit('tasklist-updated', this.id);
          this.isReordering = false; // Reset loading state when complete
        })
        .catch(err => {
          console.error('Error saving task order:', err);
          this.isReordering = false; // Reset loading state on error
          
          // Fallback to sequential updates if bulk update fails
          if (confirm('Bulk update failed. Try updating tasks one by one?')) {
            this.saveTaskOrder(tasks);
          }
        });
    },
  },
};
</script>

<style lang="scss">
.list {
  .task-list-counter {
    font-size: 10px;
  }

  p {
    margin-bottom: -6px;
    color: #817979;
  }

  h2 {
    border-bottom: 1px solid #ccc;
    padding-bottom: 6px;
    margin-bottom: 6px;
    position: relative;
    z-index: 1000;

    .list-buttons {
      position: absolute;
      font-size: 13px;
      padding-right: 3px;
      right: 0px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;

      button {
        margin-right: 3px;
      }

      .dropdown {
        position: relative;
        display: inline-block;

        .dropdown-toggle {
          margin-right: 3px;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          left: auto;
          z-index: 1000;
          display: none;
          min-width: 10rem;
          padding: 0.5rem 0;
          margin: 0.125rem 0 0;
          font-size: 0.875rem;
          color: #212529;
          text-align: left;
          background-color: #fff;
          background-clip: padding-box;
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 0.25rem;

          &.show {
            display: block;
          }

          .dropdown-item {
            display: block;
            width: 100%;
            padding: 0.25rem 1.5rem;
            clear: both;
            font-weight: 400;
            color: #212529;
            text-align: inherit;
            white-space: nowrap;
            background-color: transparent;
            border: 0;
            cursor: pointer;

            &:hover {
              color: #16181b;
              text-decoration: none;
              background-color: $col-note-hover;
            }
            &.text-danger:hover {
              background-color: $col-danger-background;
            }
          }
        }
      }
    }
  }
}

.new-task-form {
  padding-top: 8px;
  padding-bottom: 8px;
  text-align: center;

  #new-task {
    display: inline-block;
    width: 100%;
    // width: calc(100% - 64px);
  }

  .small {
    font-size: 10px;
    margin-left: 4px;
  }
}

.list-row {
  margin-left: 0;
  margin-right: 0;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 180px;
  overflow-y: auto;

  .header {
    cursor: pointer;
    font-size: 1.4rem;
  }

  .header-icon {
    opacity: 0.5;
    font-size: 18px;
    margin-left: 5px;
  }
}

@media (min-width: $breakpoint-lg) {
  .list-row {
    top: 160px;
  }

  .list {
    // min-height: 100%;
  }
}
</style>
